import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { registerPushToken } from '../services/pushNotifications';

const PushNotificationManager = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const userId = user?.userId || user?.attributes?.sub || user?.username;

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !userId) return undefined;

    let active = true;
    const listeners = [];
    const registerToken = async (token) => {
      if (!active || !token) return;
      const response = await registerPushToken(token);
      localStorage.setItem('myhomeentryPushRegistrationStatus', JSON.stringify({
        registered: true,
        platform: Capacitor.getPlatform(),
        registeredAt: new Date().toISOString()
      }));
      console.log('Push device registration succeeded:', response?.data?.message || 'registered');
    };

    const initialize = async () => {
      listeners.push(await CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) queryClient.invalidateQueries(['userNotifications', userId]);
      }));

      let permission = await FirebaseMessaging.checkPermissions();
      if (['prompt', 'prompt-with-rationale'].includes(permission.receive)) {
        permission = await FirebaseMessaging.requestPermissions();
      }
      if (permission.receive !== 'granted' || !active) return;

      listeners.push(await FirebaseMessaging.addListener('tokenReceived', event => {
        registerToken(event.token).catch(error => {
          console.error('Push token registration failed:', error);
        });
      }));

      if (Capacitor.getPlatform() === 'android') {
        await FirebaseMessaging.createChannel({
          id: 'myhomeentry_notifications',
          name: 'MyHomeEntry notifications',
          description: 'Building, visitor, payment, complaint, and security alerts',
          importance: 5,
          visibility: 1,
          sound: 'default',
          vibration: true
        });
      }

      let lastError;
      for (let attempt = 1; attempt <= 3 && active; attempt += 1) {
        try {
          const tokenResult = await FirebaseMessaging.getToken();
          if (tokenResult.token) {
            await registerToken(tokenResult.token);
            lastError = null;
            break;
          }
        } catch (error) {
          lastError = error;
          console.error(`Push token attempt ${attempt} failed:`, error);
        }
        await new Promise(resolve => setTimeout(resolve, attempt * 2000));
      }
      if (lastError) throw lastError;
      listeners.push(await FirebaseMessaging.addListener('notificationReceived', () => {
        queryClient.invalidateQueries(['userNotifications', userId]);
      }));
      listeners.push(await FirebaseMessaging.addListener('notificationActionPerformed', event => {
        const data = event.notification?.data || {};
        queryClient.invalidateQueries(['userNotifications', userId]);
        navigate(data.route || '/notifications-dashboard', {
          state: { selectedNotificationId: data.notificationId }
        });
      }));
    };

    initialize().catch(error => {
      localStorage.setItem('myhomeentryPushRegistrationStatus', JSON.stringify({
        registered: false,
        error: error?.message || String(error),
        failedAt: new Date().toISOString()
      }));
      console.error('Push notification initialization failed:', error);
    });
    return () => {
      active = false;
      listeners.forEach(listener => listener.remove());
    };
  }, [navigate, queryClient, userId]);

  return null;
};

export default PushNotificationManager;
